import React, { useState, useEffect } from 'react';
import MessageList from './MessageList';
import MessageForm from './MessageForm';
import { InAppMessageDto, CreateInAppMessageRequest, UpdateInAppMessageRequest, MessageStatsResponse } from '../../types/InAppMessage';
import { inAppMessageApi } from '../../services/api';

const InAppMessagesPage: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingMessage, setEditingMessage] = useState<InAppMessageDto | undefined>();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [stats, setStats] = useState<MessageStatsResponse | null>(null);

  useEffect(() => {
    loadStats();
  }, [refreshTrigger]);

  const loadStats = async () => {
    try {
      const statsData = await inAppMessageApi.getStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleCreateMessage = () => {
    setEditingMessage(undefined);
    setShowForm(true);
  };

  const handleEditMessage = (message: InAppMessageDto) => {
    setEditingMessage(message);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: CreateInAppMessageRequest | UpdateInAppMessageRequest) => {
    try {
      if (editingMessage) {
        await inAppMessageApi.updateMessage(editingMessage.id, data as UpdateInAppMessageRequest);
      } else {
        await inAppMessageApi.createMessage(data as CreateInAppMessageRequest);
      }
      
      setShowForm(false);
      setEditingMessage(undefined);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error saving message:', error);
      throw error;
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingMessage(undefined);
  };

  const handleDeleteMessage = (id: string) => {
    setRefreshTrigger(prev => prev + 1);
  };

  if (showForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <MessageForm
            message={editingMessage}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            isEdit={!!editingMessage}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">In-App Messages</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Manage in-app messages for The Trail App users
                </p>
              </div>
              <button
                onClick={handleCreateMessage}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Message
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Active Messages</dt>
                  <dd className="text-lg font-medium text-gray-900">{stats.totalActiveMessages}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <MessageList
          onEditMessage={handleEditMessage}
          onDeleteMessage={handleDeleteMessage}
          refreshTrigger={refreshTrigger}
        />
      </div>
    </div>
  );
};

export default InAppMessagesPage;